import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {
  Area,
  ReservationLocation,
  TableCombination,
  TableDefinition,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {TFunction} from '@wix/yoshi-flow-editor'

export const ANY_AREA_ID = '__any_area__'

export type AreaOption = {
  id: string
  name: string
  description?: string
  isAnyArea?: boolean
}

export type AreaPartySizeRange = {
  min: number
  max: number
}

export const getVisibleAreas = (reservationLocation?: ReservationLocation): Area[] => {
  const areaConfiguration = reservationLocation?.configuration?.areaConfiguration

  if (!areaConfiguration?.enabled || !areaConfiguration.visibleToGuests) {
    return []
  }

  return (areaConfiguration.areas ?? []).filter(({id, name}) => Boolean(id && name))
}

export const getAreaOptions = (
  reservationLocation: ReservationLocation | undefined,
  t: TFunction,
): AreaOption[] => {
  const visibleAreas = getVisibleAreas(reservationLocation)

  if (visibleAreas.length <= 1) {
    return []
  }

  return [
    {
      id: ANY_AREA_ID,
      name: t('uou-reservations.shared.any-area'),
      isAnyArea: true,
    },
    ...visibleAreas.map(({id, name, description}) => ({
      id: id!,
      name: name!,
      description: description ?? undefined,
    })),
  ]
}

export const getAreaDropdownOptions = (
  areaOptions: AreaOption[],
  {showDescription = true}: {showDescription?: boolean} = {},
): DropdownOptionProps[] =>
  areaOptions.map(({id, name, description}) => ({
    id,
    value: name,
    subtitle: showDescription ? description : undefined,
    isSelectable: true,
  }))

export const normalizeAreaId = (areaId?: string) =>
  areaId && areaId !== ANY_AREA_ID ? areaId : undefined

export const getSelectedAreaOption = (
  areaOptions: AreaOption[],
  selectedAreaId?: string,
): AreaOption | undefined => {
  const normalizedAreaId = normalizeAreaId(selectedAreaId)

  return (
    areaOptions.find(({id}) => id === normalizedAreaId) ??
    areaOptions.find(({isAnyArea}) => isAnyArea) ??
    areaOptions[0]
  )
}

const isActive = ({isActive}: {isActive?: boolean | null}) => isActive !== false

const hasSeatsRange = (
  table: Pick<TableDefinition | TableCombination, 'seatsMin' | 'seatsMax'>,
): table is Pick<TableDefinition | TableCombination, 'seatsMin' | 'seatsMax'> &
  Required<Pick<TableDefinition | TableCombination, 'seatsMin' | 'seatsMax'>> =>
  typeof table.seatsMin === 'number' && typeof table.seatsMax === 'number'

const isCombinationAssignedToArea = (
  combination: TableCombination,
  tableAreaById: Map<string, string | undefined>,
  areaId: string,
) => {
  const tableIds = combination.tableIds ?? []

  return tableIds.length > 0 && tableIds.every((tableId) => tableAreaById.get(tableId) === areaId)
}

export const getSelectedAreaPartySizeRange = (
  reservationLocation?: ReservationLocation,
  selectedAreaId?: string,
): AreaPartySizeRange | undefined => {
  const areaId = normalizeAreaId(selectedAreaId)

  if (!areaId) {
    return undefined
  }

  const tableDefinitions =
    reservationLocation?.configuration?.tableManagement?.tableDefinitions ?? []
  const tableAreaById = new Map(
    tableDefinitions
      .filter(({id}) => Boolean(id))
      .map(({id, areaId: tableAreaId}) => [id!, tableAreaId ?? undefined]),
  )

  const tableCandidates = tableDefinitions
    .filter((table) => isActive(table) && table.areaId === areaId)
    .filter(hasSeatsRange)

  const combinationCandidates = (
    reservationLocation?.configuration?.tableManagement?.tableCombinations ?? []
  )
    .filter((combination) => isActive(combination))
    .filter((combination) => isCombinationAssignedToArea(combination, tableAreaById, areaId))
    .filter(hasSeatsRange)

  const candidates = [...tableCandidates, ...combinationCandidates]

  if (!candidates.length) {
    return undefined
  }

  return {
    min: Math.min(...candidates.map(({seatsMin}) => seatsMin)),
    max: Math.max(...candidates.map(({seatsMax}) => seatsMax)),
  }
}

export const getSelectedAreaPartySizeErrorKey = (
  reservationLocation: ReservationLocation | undefined,
  selectedAreaId: string | undefined,
  partySize: number,
) => {
  const range = getSelectedAreaPartySizeRange(reservationLocation, selectedAreaId)

  if (!range) {
    return undefined
  }

  if (range.min > partySize) {
    return 'uou-reservations.reservations.area-min-party-size-error-with-area'
  }

  if (range.max < partySize) {
    return 'uou-reservations.reservations.area-max-party-size-error-with-area'
  }

  return undefined
}

export const getAreaNameById = (
  reservationLocation: ReservationLocation | undefined,
  areaId?: string | null,
) =>
  reservationLocation?.configuration?.areaConfiguration?.areas?.find(({id}) => id === areaId)?.name

export const getReservationSummaryLocationText = ({
  reservationLocation,
  areaId,
  t,
}: {
  reservationLocation?: ReservationLocation
  areaId?: string | null
  t: TFunction
}) => {
  const locationName = reservationLocation?.location?.name
  const areaOptions = getAreaOptions(reservationLocation, t)
  const areaName = areaId ? getAreaNameById(reservationLocation, areaId) : undefined
  const areaLabel =
    areaName ?? (areaOptions.length ? t('uou-reservations.shared.any-area') : undefined)

  return [locationName, areaLabel].filter(Boolean).join(', ')
}
